/** @typedef {import('./floorPlanDesignerPivotWallPolygons.js').DesignerGraph} DesignerGraph */
/** @typedef {import('./floorPlanDesignerState.js').DesignerWall} DesignerWall */
/** @typedef {import('./floorPlanDesignerGeometry.js').findClosedPolygons} FindClosedPolygons */

import { directedEdgeKey } from './floorPlanDesignerGeometry.js';
import { getPointById } from './floorPlanDesignerState.js';
import { getRoomIdForDetectedRoom } from './floorPlanDesignerRooms.js';

/**
 * @param {[number, number]} start
 * @param {[number, number]} end
 * @returns {{ dir: { x: number; y: number }; length: number }}
 */
function wallAxisFromPivotEdge(start, end) {
  const dx = end[0] - start[0];
  const dy = end[1] - start[1];
  const length = Math.hypot(dx, dy);
  if (length < 1e-6) {
    return { dir: { x: 1, y: 0 }, length: 0 };
  }
  return { dir: { x: dx / length, y: dy / length }, length };
}

const COLLINEAR_DOT_EPS = 0.985;

/**
 * @typedef {{
 *   wallId: string;
 *   fromPointId: string;
 *   toPointId: string;
 * }} BoundaryEdgeRef
 */

/**
 * @typedef {{
 *   boundaryWallId: string;
 *   physicalWallId: string;
 *   fromPointId: string;
 *   toPointId: string;
 *   isMirror: true;
 *   roomId: string;
 *   sourceEdgeKey: string;
 * }} MirroredBoundaryWall
 */

/**
 * @typedef {{
 *   pivotId: string;
 *   stemWallIds: [string, string];
 *   terminatorWallIds: string[];
 *   capConnections: Array<{
 *     roomId: string;
 *     terminatorWallId: string;
 *     capStemWallId: string;
 *     forwardEdge: BoundaryEdgeRef;
 *     mirrorEdge: BoundaryEdgeRef;
 *   }>;
 * }} TJunctionInfo
 */

/**
 * @param {DesignerGraph} graph
 * @param {string} pivotId
 * @param {string} wallId
 * @param {string} otherPointId
 * @returns {{ dir: { x: number; y: number }; length: number } | null}
 */
function wallDirectionFromPivot(graph, pivotId, wallId, otherPointId) {
  const pivotPt = getPointById(/** @type {import('./floorPlanDesignerState.js').DesignerState} */ (graph), pivotId);
  const otherPt = getPointById(/** @type {import('./floorPlanDesignerState.js').DesignerState} */ (graph), otherPointId);
  if (!pivotPt || !otherPt) return null;
  return wallAxisFromPivotEdge([pivotPt.x, pivotPt.y], [otherPt.x, otherPt.y]);
}

/**
 * @param {{ dir: { x: number; y: number } }} axisA
 * @param {{ dir: { x: number; y: number } }} axisB
 * @returns {boolean}
 */
function areCollinearAtPivot(axisA, axisB) {
  const dot = axisA.dir.x * axisB.dir.x + axisA.dir.y * axisB.dir.y;
  return Math.abs(dot) >= COLLINEAR_DOT_EPS;
}

/**
 * @param {Array<{ wallId: string; otherPointId: string }>} incidents
 * @param {DesignerGraph} graph
 * @param {string} pivotId
 * @returns {[string, string] | null}
 */
function findCollinearStemPair(incidents, graph, pivotId) {
  for (let i = 0; i < incidents.length; i += 1) {
    for (let j = i + 1; j < incidents.length; j += 1) {
      const axisA = wallDirectionFromPivot(graph, pivotId, incidents[i].wallId, incidents[i].otherPointId);
      const axisB = wallDirectionFromPivot(graph, pivotId, incidents[j].wallId, incidents[j].otherPointId);
      if (!axisA || !axisB) continue;
      if (areCollinearAtPivot(axisA, axisB)) {
        return [incidents[i].wallId, incidents[j].wallId];
      }
    }
  }
  return null;
}

/**
 * @param {ReturnType<typeof import('./floorPlanDesignerGeometry.js').findClosedPolygons>[number]} room
 * @param {string} pivotId
 * @param {string} wallId
 * @returns {BoundaryEdgeRef | null}
 */
function getRoomBoundaryEdgeAtPivot(room, pivotId, wallId) {
  for (const edge of room.boundaryEdges || []) {
    if (edge.wallId !== wallId) continue;
    if (edge.fromPointId === pivotId || edge.toPointId === pivotId) {
      return edge;
    }
  }
  return null;
}

/**
 * @param {ReturnType<typeof import('./floorPlanDesignerGeometry.js').findClosedPolygons>[number]} room
 * @param {string} pivotId
 * @param {string} terminatorWallId
 * @param {[string, string]} stemWallIds
 * @returns {{ stemWallId: string; forwardEdge: BoundaryEdgeRef } | null}
 */
function findCapStemAtPivot(room, pivotId, terminatorWallId, stemWallIds) {
  const edges = room.boundaryEdges || [];
  const stemSet = new Set(stemWallIds);

  for (let i = 0; i < edges.length; i += 1) {
    const edge = edges[i];
    if (edge.wallId !== terminatorWallId) continue;
    if (edge.fromPointId !== pivotId && edge.toPointId !== pivotId) continue;

    const prev = edges[(i - 1 + edges.length) % edges.length];
    const next = edges[(i + 1) % edges.length];

    for (const candidate of [prev, next]) {
      if (!stemSet.has(candidate.wallId)) continue;
      const forwardEdge = getRoomBoundaryEdgeAtPivot(room, pivotId, candidate.wallId);
      if (forwardEdge) {
        return { stemWallId: candidate.wallId, forwardEdge };
      }
    }
  }

  return null;
}

/**
 * @param {BoundaryEdgeRef} edge
 * @returns {BoundaryEdgeRef}
 */
export function mirrorBoundaryEdge(edge) {
  return {
    wallId: edge.wallId,
    fromPointId: edge.toPointId,
    toPointId: edge.fromPointId,
  };
}

/**
 * @param {Map<string, Array<{ wallId: string; otherPointId: string }>>} adj
 * @param {ReturnType<typeof import('./floorPlanDesignerGeometry.js').findClosedPolygons>} rooms
 * @param {DesignerGraph} graph
 * @returns {TJunctionInfo[]}
 */
export function detectTJunctions(adj, rooms, graph) {
  /** @type {TJunctionInfo[]} */
  const junctions = [];

  for (const [pivotId, incidents] of adj.entries()) {
    if (incidents.length < 3) continue;

    const stemPair = findCollinearStemPair(incidents, graph, pivotId);
    if (!stemPair) continue;

    const stemSet = new Set(stemPair);
    const terminatorWallIds = incidents
      .map((item) => item.wallId)
      .filter((wallId) => !stemSet.has(wallId));

    if (terminatorWallIds.length === 0) continue;

    /** @type {TJunctionInfo['capConnections']} */
    const capConnections = [];

    for (const room of rooms) {
      const roomId = getRoomIdForDetectedRoom(room);
      for (const terminatorWallId of terminatorWallIds) {
        const termEdge = getRoomBoundaryEdgeAtPivot(room, pivotId, terminatorWallId);
        if (!termEdge) continue;

        const capStem = findCapStemAtPivot(room, pivotId, terminatorWallId, stemPair);
        if (!capStem) continue;

        capConnections.push({
          roomId,
          terminatorWallId,
          capStemWallId: capStem.stemWallId,
          forwardEdge: capStem.forwardEdge,
          mirrorEdge: mirrorBoundaryEdge(capStem.forwardEdge),
        });
      }
    }

    junctions.push({
      pivotId,
      stemWallIds: stemPair,
      terminatorWallIds,
      capConnections,
    });
  }

  return junctions;
}

/**
 * @param {TJunctionInfo[]} tJunctions
 * @returns {MirroredBoundaryWall[]}
 */
export function deriveMirroredBoundaryWalls(tJunctions) {
  /** @type {MirroredBoundaryWall[]} */
  const boundaryWalls = [];
  /** @type {Set<string>} */
  const seen = new Set();

  for (const junction of tJunctions) {
    for (const cap of junction.capConnections) {
      const sourceEdgeKey = directedEdgeKey(
        cap.forwardEdge.wallId,
        cap.forwardEdge.fromPointId,
        cap.forwardEdge.toPointId,
      );
      const boundaryWallId = `${cap.capStemWallId}:mirror:${cap.roomId}`;
      if (seen.has(boundaryWallId)) continue;
      seen.add(boundaryWallId);
      boundaryWalls.push({
        boundaryWallId,
        physicalWallId: cap.capStemWallId,
        fromPointId: cap.mirrorEdge.fromPointId,
        toPointId: cap.mirrorEdge.toPointId,
        isMirror: true,
        roomId: cap.roomId,
        sourceEdgeKey,
      });
    }
  }

  return boundaryWalls;
}

/**
 * @param {Map<string, Array<{ wallId: string; otherPointId: string }>>} adj
 * @param {ReturnType<typeof import('./floorPlanDesignerGeometry.js').findClosedPolygons>} rooms
 * @param {DesignerGraph} graph
 * @returns {{ tJunctions: TJunctionInfo[]; mirroredBoundaryWalls: MirroredBoundaryWall[] }}
 */
export function buildBoundaryWallBundle(adj, rooms, graph) {
  const tJunctions = detectTJunctions(adj, rooms, graph);
  const mirroredBoundaryWalls = deriveMirroredBoundaryWalls(tJunctions);
  return { tJunctions, mirroredBoundaryWalls };
}

/**
 * @param {ReturnType<typeof import('./floorPlanDesignerGeometry.js').findClosedPolygons>} rooms
 * @param {MirroredBoundaryWall[]} mirroredBoundaryWalls
 * @returns {Array<{ wallId: string; roomId: string; directedEdgeKey: string; isMirror: boolean }>}
 */
export function getRoomBoundaryRefsWithMirrors(rooms, mirroredBoundaryWalls) {
  /** @type {Array<{ wallId: string; roomId: string; directedEdgeKey: string; isMirror: boolean }>} */
  const refs = [];
  for (const room of rooms) {
    const roomId = getRoomIdForDetectedRoom(room);
    for (const edge of room.boundaryEdges || []) {
      refs.push({
        wallId: edge.wallId,
        roomId,
        directedEdgeKey: directedEdgeKey(edge.wallId, edge.fromPointId, edge.toPointId),
        isMirror: false,
      });
    }
  }
  for (const mirror of mirroredBoundaryWalls) {
    refs.push({
      wallId: mirror.physicalWallId,
      roomId: mirror.roomId,
      directedEdgeKey: directedEdgeKey(mirror.physicalWallId, mirror.fromPointId, mirror.toPointId),
      isMirror: true,
    });
  }
  return refs;
}

/**
 * @param {string} pivotId
 * @param {TJunctionInfo[]} tJunctions
 * @returns {TJunctionInfo | null}
 */
export function getTJunctionAtPivot(pivotId, tJunctions) {
  return tJunctions.find((item) => item.pivotId === pivotId) ?? null;
}
